import type {
  AdminUserDetail,
  AdminUserPause,
  AdminUserSubscription,
  AdminUserWebFunnelChannel,
} from "api/adminApi";

// 회원관리 화면의 표시 변환 모음. JSX 없이 순수 함수만 둔다 (테스트 대상).
// AdminPage.tsx 안에도 같은 이름의 로컬 헬퍼가 있지만 가져다 쓰지 않는다 —
// 회원관리는 체험 미리보기가 다루지 않던 상태(휴면·종결 권고 등)까지 표시해야 해서 매핑이 다르다.

const PROGRESS_STATUS_LABEL: Record<string, string> = {
  preparing: "준비 중",
  in_progress: "이용 중",
  terminated: "종료",
  recommended_stop: "종결 권고",
  leave: "탈퇴",
  auto_stop: "자동 종료",
};

export const progressStatusLabel = (status?: string | null) =>
  (status && PROGRESS_STATUS_LABEL[status]) || status || "알 수 없음";

const SUBSCRIPTION_STATUS_LABEL: Record<string, string> = {
  freetrial: "체험 중",
  freetrial_expired: "체험 만료",
  subscribing: "이용 중",
  subscribing_expired: "이용 종료",
};

export const subscriptionStatusLabel = (status?: string | null) =>
  (status && SUBSCRIPTION_STATUS_LABEL[status]) || status || "없음";

const PLAN_LABEL: Record<string, string> = {
  starter: "스타터",
  challenger: "챌린저",
  master: "마스터",
};

export const planLabel = (plan?: string | null) =>
  (plan && PLAN_LABEL[plan]) || plan || "없음";

const OAUTH_ISSUER_LABEL: Record<string, string> = {
  kakao: "카카오",
  google: "구글",
  apple: "애플",
  naver: "네이버",
};

export const oauthIssuerLabel = (issuer?: string | null) =>
  (issuer && OAUTH_ISSUER_LABEL[issuer]) || issuer || "알 수 없음";

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  ready: "대기",
  success: "승인 대기",
  confirmed: "결제 완료",
  canceled: "취소",
  failed: "실패",
};

export const paymentStatusLabel = (status?: string | null) =>
  (status && PAYMENT_STATUS_LABEL[status]) || status || "알 수 없음";

const VENDOR_TYPE_LABEL: Record<string, string> = {
  toss_payments: "토스",
  app_store: "앱스토어",
  play_store: "플레이스토어",
  steppay: "스텝페이",
};

export const vendorTypeLabel = (vendor?: string | null) =>
  (vendor && VENDOR_TYPE_LABEL[vendor]) || vendor || "알 수 없음";

// 정지 상태 한 줄. 무기한이면 종료 예정일이 없다는 것을 분명히 말해 준다.
export const pauseStateLabel = (pause: AdminUserPause) => {
  if (!pause.isPaused) return "정지 아님";
  if (!pause.resumedAt) return "무기한 정지";
  return `${formatDate(pause.resumedAt)} 재개 예정`;
};

// "2026-08-27T14:03:22" 또는 "2026-08-27" 을 "2026. 8. 27." 로. 값이 없으면 빈 문자열.
export const formatDate = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const formatDateTime = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatAmount = (amount?: number | null) =>
  amount == null ? "" : `${amount.toLocaleString("ko-KR")}원`;

// yyyy-MM-dd — date 인풋 값과 서버 파라미터가 같은 형식이다.
export const toDateInputValue = (date: Date) => {
  const pad = (n: number) => `${n}`.padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const todayInputValue = () => toDateInputValue(new Date());

// 프로그램 시작 팝업의 기본 이용 종료일 — 오늘부터 100일.
// 서버가 untilDate 를 비우면 쓰는 기본값(AdminUserBO.DEFAULT_START_DAYS)과 같아야 화면과 결과가 어긋나지 않는다.
export const defaultStartUntilValue = () => {
  const date = new Date();
  date.setDate(date.getDate() + 100);
  return toDateInputValue(date);
};

/**
 * 재개 시 자동 복원될 만료일. 서버 계산과 같은 식이다:
 *   새 만료일 = 현재 만료일 + (정지 일수) + (추가 일수)
 * 화면이 이 값을 미리 보여줘야 운영자가 "직접 지정"이 필요한 경우를 알아본다
 * (옛 스크립트로 정지된 회원은 만료일이 이미 정지 시점으로 밀려 있어 자동 계산이 맞지 않는다).
 */
export const restoredExpirationDate = (
  subscription: AdminUserSubscription,
  pause: AdminUserPause,
  extraDays: number
): string => {
  const base = subscription.subscriptionExpirationDate;
  if (!base) return "";
  const date = new Date(base);
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() + (pause.pausedDays ?? 0) + extraDays);
  return toDateInputValue(date);
};

// 이용 상태 배지 톤 — 색은 장식이 아니라 상태를 읽는 축이다.
export const subscriptionTone = (
  pause: AdminUserPause,
  subscription: AdminUserSubscription
): "ok" | "warn" | "idle" | "info" => {
  if (pause.isPaused) return "warn";
  if (
    subscription.status === "subscribing" ||
    subscription.status === "freetrial"
  )
    return "ok";
  if (!subscription.hasSubscription) return "info";
  return "idle";
};

// 어드민이 이 회원에게 할 수 있는 처리. 서버 엔드포인트와 1:1 이다.
// refundTerminate 만 액션 바에 버튼이 없다 — 프로그램 종료 팝업의 "환불도 같이" 체크가 이 처리다.
export type AdminUserAction =
  | "start"
  | "reset"
  | "refundTerminate"
  | "refundDowngrade"
  | "refundAmount"
  | "extend"
  | "pause"
  | "resume"
  | "terminate"
  | "nickname"
  | "coach";

/**
 * 이 회원에게 이 처리를 할 수 없는 이유. 할 수 있으면 null.
 *
 * 판정 기준을 서버(AdminUserBO)가 실제로 던지는 전제조건에 맞춘다. 화면이 서버보다 넓게 막으면
 * 정상 처리를 눌러 보지도 못하고, 좁게 막으면 눌러야만 안 된다는 걸 알게 된다.
 * 만료일은 서버가 시각까지 보는데 화면은 날짜만 받으므로, "오늘 만료" 처럼 애매한 경우는 막지 않고
 * 서버가 판정하게 둔다.
 */
/**
 * 어드민 환불의 기준 결제. 서버(AdminUserBO.refundPlanOf)와 같이 **가장 최근 확정 건**을 잡는다 —
 * 재구독 회원은 확정 결제가 여러 건이라 옛 건을 잡으면 화면과 실제가 어긋난다.
 * 환불 가능 판정은 서버가 내려준 isRefundable 을 그대로 쓴다 (paymentKey 는 화면에 안 내려온다).
 */
export const refundBasePayment = (detail: AdminUserDetail) => {
  const refundable = detail.payments.filter((payment) => payment.isRefundable);
  if (refundable.length === 0) return null;
  return refundable.reduce((latest, payment) =>
    (payment.confirmedAt ?? "") > (latest.confirmedAt ?? "") ? payment : latest
  );
};

/**
 * 상태를 바꾸는 종료(진행 종료 + 이용 기한 당김)가 가능한 회원인지.
 *
 * false 면 이 회원에게 남은 건 환불뿐이다 — 이미 만료·종료된 회원도 환불 요청은 들어오고,
 * 서버 refundPlanOf(TERMINATE) 는 결제와 구독 행만 보지 진행 상태를 보지 않는다.
 */
export const canTerminateState = (detail: AdminUserDetail): boolean =>
  (detail.subscription.status === "subscribing" &&
    detail.user.progressStatus === "in_progress") ||
  detail.pause.isPaused;

/**
 * 협의 금액이 환불 가능 잔액을 넘는지. 넘으면 안내 문구, 아니면 null.
 *
 * 잔액은 원결제 금액이 아니라 **현재 payAmount** 다 — 부분 취소(다운그레이드·금액만 환불)가 있었으면
 * 웹훅이 그만큼 줄여 둔다. 395,000 결제 후 100,000 을 다운그레이드로 돌려줬으면 잔액은 295,000 이고
 * 전액 환불도 295,000 이다.
 *
 * 입력값을 잔액으로 조용히 잘라 버리는 방법도 있지만, 돈이 걸린 칸에서 타이핑한 숫자가 말없이
 * 바뀌면 운영자가 얼마를 보낸 건지 확신할 수 없다. 그대로 두고 왜 못 보내는지 말한다.
 */
export const refundAmountError = (
  amount: string,
  balance?: number | null
): string | null => {
  const value = Number(amount);
  if (!amount.trim() || value <= 0) return null;
  return value > (balance ?? 0)
    ? `환불 가능 잔액 ${formatAmount(balance)}을 넘을 수 없어요`
    : null;
};

export const actionBlockReason = (
  detail: AdminUserDetail,
  action: AdminUserAction
): string | null => {
  const { user, subscription, pause, coach } = detail;

  if (user.progressStatus === "leave") return "탈퇴한 계정이에요";

  // 구독 행과 만료일이 있어야 기간을 다룰 수 있다. 준비 중(결제 전) 회원이 여기 걸린다.
  const hasSubscription =
    subscription.hasSubscription && !!subscription.subscriptionExpirationDate;
  // 서버 pause 의 전제조건과 같은 식이다.
  const isSubscribing =
    subscription.status === "subscribing" &&
    user.progressStatus === "in_progress";

  switch (action) {
    // 서버 start 의 전제조건과 같다 — 준비 중이면서 구독 행이 아직 없어야 한다.
    // 구독 행 PK 가 userId 라, 이미 있는 회원을 시작시키면 기존 이력을 덮어쓴다.
    case "start":
      if (user.progressStatus !== "preparing")
        return "준비 중인 회원만 시작할 수 있어요";
      if (subscription.hasSubscription)
        return "이미 구독 정보가 있어요. 기간 연장으로 처리해 주세요";
      if (!user.nickname) return "닉네임을 아직 정하지 않은 회원이에요";
      return null;

    // 서버 reset 의 전제조건과 같다. 시작한 적 없는 회원은 지울 것도 없고,
    // 코치·채팅방 없이 활동지만 열리면 반쪽 상태가 된다.
    case "reset":
      return user.progressStatus === "preparing"
        ? "아직 시작하지 않은 회원이에요"
        : null;

    case "extend":
      return hasSubscription ? null : "이용 정보가 없는 회원이에요";

    case "pause":
      if (pause.isPaused) return "이미 일시정지 중이에요";
      if (!isSubscribing) return "이용 중인 회원만 정지할 수 있어요";
      if (isExpired(subscription.subscriptionExpirationDate))
        return "이용 기한이 이미 지났어요";
      return null;

    case "resume":
      return pause.isPaused ? null : "일시정지 중인 회원이 아니에요";

    // 일시정지는 내부적으로 progressStatus 가 종료로 저장될 뿐 운영자가 보는 상태는 "정지 중" 이라,
    // 무기한 정지 회원의 프로그램 종료는 열어 둔다.
    case "terminate":
      if (!hasSubscription) return "이용 정보가 없는 회원이에요";
      if (canTerminateState(detail)) return null;
      // 상태는 더 바꿀 게 없어도 환불은 남아 있을 수 있다. 그때는 팝업이 환불 전용으로 열린다 —
      // 만료된 회원의 환불 요청을 화면이 막으면 CS 가 토스 콘솔로 돌아가야 한다.
      return refundBasePayment(detail)
        ? null
        : "이용 중이거나 정지 중인 회원만 종료할 수 있어요";

    // 환불 3종은 서버 refundPlanOf 의 전제조건과 같게 막는다.
    case "refundTerminate":
    case "refundDowngrade":
    case "refundAmount": {
      const base = refundBasePayment(detail);
      if (!base) return "환불할 수 있는 토스 결제 건이 없어요";
      if (action === "refundTerminate" && !subscription.hasSubscription) {
        // 웹훅 종료 경로가 구독 행을 반드시 찾는다 — 없으면 결제만 취소되고 웹훅이 죽는다
        return "이용 정보가 없는 회원이에요";
      }
      if (action === "refundDowngrade") {
        if (user.isCarePass)
          return "케어패스 회원은 차액을 자동 계산할 수 없어요";
        // 스타터가 최하위 플랜이다. hints 도 비어 있지만(결제액보다 싼 플랜이 없다) 그건
        // 결제 금액이 모자란 경우와 같은 결과라, 왜 막혔는지 문구로 구분해 준다.
        if (user.plan === "starter") return "이미 가장 낮은 플랜이에요";
        if (base.planChangeHints.length === 0)
          return "내릴 수 있는 하위 플랜이 없어요";
      }
      return null;
    }

    case "coach":
      return coach ? null : "배정된 코치가 없어요";

    // 닉네임은 이용 상태와 무관하게 언제든 바꿀 수 있다.
    case "nickname":
    default:
      return null;
  }
};

// 만료일이 오늘보다 확실히 앞인지. 오늘이면 시각을 알 수 없으므로 지났다고 보지 않는다.
const isExpired = (expirationDate?: string | null) => {
  if (!expirationDate) return true;
  const expiration = new Date(expirationDate);
  if (Number.isNaN(expiration.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expiration.setHours(0, 0, 0, 0);
  return expiration.getTime() < today.getTime();
};

// 웹퍼널 utm 파라미터를 한 줄로 합친다. 어떤 조합이 비어 있어도 오해가 없도록 키를 붙여서 낸다
// (값만 나열하면 medium 하나만 있을 때 source 로 읽힌다). 값이 하나도 없으면 null — 줄 자체를 뺀다.
export const utmSummary = (
  webFunnel: AdminUserWebFunnelChannel
): string | null => {
  const pairs: [string, string | null | undefined][] = [
    ["source", webFunnel.utmSource],
    ["medium", webFunnel.utmMedium],
    ["campaign", webFunnel.utmCampaign],
    ["content", webFunnel.utmContent],
    ["term", webFunnel.utmTerm],
  ];
  const parts = pairs
    .filter(([, value]) => !!value?.trim())
    .map(([key, value]) => `${key}=${value!.trim()}`);
  return parts.length ? parts.join(" · ") : null;
};
